import React, { useState, useCallback, useEffect } from "react";
import Button from "react-bootstrap/Button";
import AllderTable from "../../../../components/AllderTable";
import { createTableColumns } from "../../../../utils/createTableColumns";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { branchKitchenHandler } from "../../../../redux/handlers";
import { useGlobalTable } from "../../../../hooks/useGlobalTable";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import SuccessModal from "./modals/SuccessModal";
import CategoryIncludeModal from "./modals/CategoryIncludeModal";
import { useKitchenRefresh } from "./KitchenRefreshContext";

export default function KitchenCategoryManagement() {
  const dispatch = useDispatch();
  const { branchID, kitchenID, businessSlug } = useParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { refreshTrigger, refreshAllKitchenData } = useKitchenRefresh();

  const [modalcategorylistShow, setModalcategorylistShow] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 1270,
    height: 680,
    pin: false,
    mode: "normal",
    modalname: "edit",
  });

  const fetchKitchenCategories = useCallback(
    (params) =>
      dispatch(
        branchKitchenHandler.getBranchKitchenCategories(kitchenID, {
          page: params.page ?? 1,
          perPage: params.per_page ?? 10,
          s: params.s,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          timestamp: Date.now(),
        })
      ),
    [kitchenID, dispatch]
  );

  const {
    data: categories,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    filterParams,
    setFilterParams,
    refreshData,
  } = useGlobalTable({
    fetchData: fetchKitchenCategories,
    defaultSort: "created_at",
    defaultOrder: "desc",
    defaultPageSize: 10,
  });

  useEffect(() => {
    refreshData();
  }, [refreshTrigger, refreshData]);

  const forceRefresh = useCallback(() => {
    refreshAllKitchenData();
  }, [refreshAllKitchenData]);

  const columns = createTableColumns(
    [
      {
        field: "mainCategory",
        headerName: "หมวดหมู่หลัก",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <div>{row.productSubCategory?.productCategory?.name || ""}</div>
        ),
      },
      {
        field: "subCategory",
        headerName: "หมวดหมู่ย่อย",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <div>{row.productSubCategory?.name || ""}</div>
        ),
      },
      {
        field: "productCount",
        headerName: "จำนวนสินค้า",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <div>{row.product ? row.product.length : 0}</div>
        ),
      },
      {
        field: "actions",
        headerName: "จัดการ",
        width: 100,
        sortable: false,
        type: "actions",
      },
    ],
    (row) => (
      <div className="">
        <Button
          type="button"
          className="btn-delete-st-3"
          onClick={() => handleDelete(row)}
        />
      </div>
    )
  );

  const handleDelete = (category) => {
    const categoryName =
      category?.productSubCategory?.name ||
      category?.subCategory?.name ||
      "หมวดหมู่นี้";
    setSelectedCategory({
      id: category.id,
      name: categoryName,
    });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedCategory) {
      try {
        await dispatch(
          branchKitchenHandler.deleteBranchKitchenCategory(selectedCategory.id)
        );
        setSuccessMessage("ลบหมวดหมู่ครัวสำเร็จ");
        setShowSuccessModal(true);

        setTimeout(() => {
          forceRefresh();
          window.location.reload();
        }, 300);
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
    setShowDeleteModal(false);
  };

  const handleSubmit = async (selectedCategories) => {
    try {
      await dispatch(
        branchKitchenHandler.createBranchKitchenCategory({
          branchKitchenId: parseInt(kitchenID),
          productSubCategoryIds: selectedCategories
            .map((category) => category.id)
            .join(","),
        })
      );

      setModalcategorylistShow((prev) => ({ ...prev, open: false }));
      setSuccessMessage("เพิ่มหมวดหมู่สำเร็จ");
      setShowSuccessModal(true);

      setTimeout(() => {
        forceRefresh();
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Error adding categories to kitchen:", error);
    }
  };

  const dataWithIndex = categories?.map((item, index) => ({
    ...item,
    index: index + 1,
  }));

  return (
    <div>
      <div className="card card-st-2">
        <div className="card-body px-0 py-4">
          <div className="d-flex align-items-center px-4 mb-4">
            <div className="tc-7 fs-20 fw-6 me-auto">หมวดหมู่</div>
            <div className="me-3">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหาชื่อหมวดหมู่"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
            <Button
              type="button"
              className="btn btn-st-7"
              onClick={() =>
                setModalcategorylistShow((prev) => ({ ...prev, open: true }))
              }
            >
              เพิ่มหมวดหมู่
            </Button>
          </div>

          <AllderTable
            data={dataWithIndex || []}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
            pageSizeOptions={[5, 10, 20]}
          />
        </div>
      </div>

      <CategoryIncludeModal
        show={modalcategorylistShow}
        onHide={() =>
          setModalcategorylistShow((prev) => ({ ...prev, open: false }))
        }
        handleSubmit={handleSubmit}
        modal={modalcategorylistShow}
        setModal={setModalcategorylistShow}
        businessSlug={businessSlug}
        triggerRefresh={forceRefresh}
        branchId={branchID}
        branchKitchenId={kitchenID}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        onSuccess={handleConfirmDelete}
        displayName={selectedCategory?.name}
        categoryContext={true}
      />

      <SuccessModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          forceRefresh();
        }}
        textsuccess={successMessage}
      />
    </div>
  );
}
